import type { PositionRow } from '@/components/finest-custom-position-list/types'
import { ulid } from 'ulid'
import { assetGroupKeys, positionCategories } from './categories'
import { incomeGroupKeys } from './incomeSuggestions'
import type { AssetGroupKey, CreditPerson, IncomeGroupKey, PositionCategory } from './types'

type AnnouncedPositionData = App.Data.SelfDisclosure.AnnouncedPositionData
type AssetPositionData = App.Data.SelfDisclosure.AssetPositionData
type FreePositionData = App.Data.SelfDisclosure.FreePositionData
type IncomePositionData = App.Data.SelfDisclosure.IncomePositionData

/** The wording a waiting row carries: in its empty cells, and on hover naming the document. */
export interface AnnouncedLabels {
  placeholder: string
  hint: (source: string) => string
}

export function amountOf(source: unknown, key: string): number | null {
  const value = (source as Record<string, unknown> | null | undefined)?.[key]

  return typeof value === 'number' ? value : null
}

function toAmount(value: unknown): number | null {
  return typeof value === 'number' ? value : null
}

function categoryPositions(person: CreditPerson, category: PositionCategory): FreePositionData[] {
  return { expenses: person.expenditures, liabilities: person.liabilities }[category]
}

export function yearCells(years: number[], amountForYear: (year: number) => number | null) {
  return Object.fromEntries(years.map((year) => [String(year), amountForYear(year)]))
}

function incomeRow(position: IncomePositionData, years: number[]): PositionRow {
  return {
    key: position.id ?? ulid(),
    id: position.id ?? null,
    cells: {
      label: position.name ?? '',
      ...yearCells(years, (year) => amountOf(position.amounts, String(year))),
    },
  }
}

function assetRow(stored: AssetPositionData): PositionRow {
  return {
    key: stored.id ?? ulid(),
    id: stored.id ?? null,
    predefinedKey: stored.type,
    cells: {
      label: stored.label ?? '',
      amount: toAmount(stored.amount),
      institute: stored.institute ?? null,
      units: stored.units ?? null,
      market_value: toAmount(stored.market_value),
    },
  }
}

function freePositionRow(position: FreePositionData): PositionRow {
  return {
    key: position.id ?? ulid(),
    id: position.id ?? null,
    cells: { label: position.name ?? '', amount: toAmount(position.amount) },
  }
}

const sameName = (cell: unknown, name: string): boolean =>
  String(cell ?? '')
    .trim()
    .toLowerCase() === name.toLowerCase()

const announcedKey = (announced: AnnouncedPositionData): string =>
  ['announced', announced.field, announced.group ?? '', announced.name].join(':')

function waitingRow(row: PositionRow, announced: AnnouncedPositionData, labels: AnnouncedLabels): PositionRow {
  return {
    ...row,
    state: 'reading',
    statePlaceholder: labels.placeholder,
    stateHint: labels.hint(announced.source),
  }
}

interface AnnouncementRules {
  matches: (row: PositionRow, announced: AnnouncedPositionData) => boolean
  blankRow: (announced: AnnouncedPositionData) => PositionRow
}

/**
 * Marks the row a document is about, or adds it as a preview when it does not exist yet.
 * Marking beats adding, so one name never shows up twice.
 */
function withAnnouncements(
  rows: PositionRow[],
  announcements: AnnouncedPositionData[],
  labels: AnnouncedLabels,
  rules: AnnouncementRules,
): PositionRow[] {
  return announcements.reduce((current, announced) => {
    const existing = current.find((row) => rules.matches(row, announced))

    return existing === undefined
      ? [
          ...current,
          waitingRow(
            { ...rules.blankRow(announced), key: announcedKey(announced), placeholder: true },
            announced,
            labels,
          ),
        ]
      : current.map((row) => (row === existing ? waitingRow(row, announced, labels) : row))
  }, rows)
}

const announcementsFor = (
  announcements: AnnouncedPositionData[],
  field: string,
  group: string | null = null,
): AnnouncedPositionData[] =>
  announcements.filter((announced) => announced.field === field && announced.group === group)

export interface CreditPositionState {
  incomeRows: Record<string, Record<IncomeGroupKey, PositionRow[]>>
  assetRows: Record<string, Record<AssetGroupKey, PositionRow[]>>
  positions: Record<PositionCategory, Record<string, PositionRow[]>>
}

export function seedIncomeGroups(
  positions: IncomePositionData[],
  years: number[],
  announcements: AnnouncedPositionData[],
  labels: AnnouncedLabels,
): Record<IncomeGroupKey, PositionRow[]> {
  return Object.fromEntries(
    incomeGroupKeys.map((group) => [
      group,
      withAnnouncements(
        positions.filter((position) => position.group === group).map((position) => incomeRow(position, years)),
        announcementsFor(announcements, 'income', group),
        labels,
        {
          matches: (row, announced) => sameName(row.cells.label, announced.name),
          blankRow: (announced) => ({
            key: ulid(),
            cells: { label: announced.name, ...yearCells(years, () => null) },
          }),
        },
      ),
    ]),
  ) as Record<IncomeGroupKey, PositionRow[]>
}

function seedAssetGroups(
  person: CreditPerson,
  announcements: AnnouncedPositionData[],
  labels: AnnouncedLabels,
): Record<AssetGroupKey, PositionRow[]> {
  return Object.fromEntries(
    assetGroupKeys.map((group) => [
      group,
      withAnnouncements(
        person.assets.filter((position) => position.group === group).map(assetRow),
        announcementsFor(announcements, 'assets', group),
        labels,
        {
          matches: (row, announced) => row.predefinedKey === announced.name,
          blankRow: (announced) => ({ ...blankAssetRow(), predefinedKey: announced.name }),
        },
      ),
    ]),
  ) as Record<AssetGroupKey, PositionRow[]>
}

function seedFreePositions(person: CreditPerson, category: PositionCategory, labels: AnnouncedLabels): PositionRow[] {
  return withAnnouncements(
    categoryPositions(person, category).map(freePositionRow),
    announcementsFor(person.announcedPositions, category),
    labels,
    {
      matches: (row, announced) => sameName(row.cells.label, announced.name),
      blankRow: (announced) => ({ key: ulid(), cells: { label: announced.name, amount: null } }),
    },
  )
}

export function seedCreditState(people: CreditPerson[], years: number[], labels: AnnouncedLabels): CreditPositionState {
  const incomeRows: CreditPositionState['incomeRows'] = {}
  const assetRows: CreditPositionState['assetRows'] = {}
  const positions: CreditPositionState['positions'] = { expenses: {}, liabilities: {} }

  people.forEach((person) => {
    incomeRows[person.id] = seedIncomeGroups(person.incomes, years, person.announcedPositions, labels)
    assetRows[person.id] = seedAssetGroups(person, person.announcedPositions, labels)

    positionCategories.forEach((category) => {
      positions[category][person.id] = seedFreePositions(person, category, labels)
    })
  })

  return { incomeRows, assetRows, positions }
}

export function blankIncomeRow(years: number[]): PositionRow {
  return { key: ulid(), cells: { label: '', ...yearCells(years, () => null) } }
}

export function blankAssetRow(): PositionRow {
  return {
    key: ulid(),
    predefinedKey: null,
    cells: { label: '', amount: null, institute: null, units: null, market_value: null },
  }
}

export function blankPositionRow(): PositionRow {
  return { key: ulid(), cells: { label: '', amount: null } }
}

export function cellNumber(row: PositionRow | undefined, key: string): number | null {
  return typeof row?.cells[key] === 'number' ? (row.cells[key] as number) : null
}

export function cellText(row: PositionRow | undefined, key: string): string | null {
  const value = row?.cells[key]

  return typeof value === 'string' && value.trim() !== '' ? value : null
}

function rowLabel(row: PositionRow): string {
  return String(row.cells.label ?? '')
}

const hasName = (row: PositionRow): boolean => rowLabel(row).trim() !== ''

const hasAssetData = (row: PositionRow): boolean =>
  Boolean(row.predefinedKey) ||
  cellText(row, 'label') !== null ||
  cellText(row, 'institute') !== null ||
  ['amount', 'units', 'market_value'].some((key) => cellNumber(row, key) !== null)

const hasNumber = (row: PositionRow): boolean =>
  Object.entries(row.cells).some(([cell, value]) => cell !== 'label' && typeof value === 'number')

export const isPositionDraft = (row: PositionRow): boolean =>
  Boolean(row.placeholder) || (!hasName(row) && !hasNumber(row))

export const isAssetDraft = (row: PositionRow): boolean => Boolean(row.placeholder) || !hasAssetData(row)

export const incomePayload = (row: PositionRow, group: IncomeGroupKey, years: number[]) => ({
  group,
  name: rowLabel(row),
  amounts: yearCells(years, (year) => cellNumber(row, String(year))),
})

export const freePositionPayload = (row: PositionRow) => ({
  name: rowLabel(row),
  amount: cellNumber(row, 'amount'),
})

export const assetPayload = (row: PositionRow, group: AssetGroupKey) => ({
  group,
  type: row.predefinedKey ?? null,
  label: row.predefinedKey ? null : cellText(row, 'label'),
  amount: cellNumber(row, 'amount'),
  institute: cellText(row, 'institute'),
  units: cellNumber(row, 'units'),
  market_value: cellNumber(row, 'market_value'),
})
